import { memo, useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";

type QaPair = {
  linkId?: string;
  question: string;
  questionHelper?: string;
  answer: string;
};

function formatAnswerLine(answer: fhir5.QuestionnaireResponseItemAnswer): string {
  if (answer.valueString !== undefined && answer.valueString !== null) {
    return String(answer.valueString);
  }
  if (answer.valueBoolean !== undefined && answer.valueBoolean !== null) {
    return answer.valueBoolean ? "Sim" : "Não";
  }
  if (answer.valueCoding) {
    return (
      answer.valueCoding.display ??
      answer.valueCoding.code ??
      ""
    ).trim();
  }
  return "";
}

function extractQaPairs(
  qr: fhir5.QuestionnaireResponse | undefined,
): QaPair[] {
  if (!qr) return [];
  const pairs: QaPair[] = [];

  const walkItems = (items: fhir5.QuestionnaireResponseItem[] | undefined) => {
    for (const item of items ?? []) {
      if (item.item?.length) {
        walkItems(item.item);
        continue;
      }

      const answers = (item.answer ?? [])
        .map(formatAnswerLine)
        .filter((s) => s.length > 0);

      if (answers.length === 0 && !item.text) continue;
      const linkId = item.linkId?.trim();
      pairs.push({
        linkId: linkId || undefined,
        question: item.text?.trim() || linkId || "Pergunta",
        answer: answers.join(", ") || "—",
      });
    }
  };

  walkItems(qr.item);
  return pairs;
}

function questionnaireFallbackTitle(qr: fhir5.QuestionnaireResponse): string {
  const ref =
    typeof qr.questionnaire === "string"
      ? qr.questionnaire
      : (qr.questionnaire as { reference?: string } | undefined)?.reference;

  const t = String(ref ?? "").trim();
  if (!t) return "Questionário";
  return t;
}

function QuestionAnswerBlock(props: {
  questionnaireId: string;
  index: number;
  question: string;
  questionHelper?: string;
  answer: string;
}) {
  const theme = useTheme();
  const titleId = `patient-q-selected-${props.questionnaireId}-q-${props.index}-title`;

  const answerSurfaceSx = useMemo(
    () => ({
      mt: 1.5,
      p: 1.5,
      borderRadius: 1,
      border: 1,
      borderColor: "divider",
      bgcolor:
        theme.palette.mode === "dark"
          ? alpha(theme.palette.common.white, 0.06)
          : theme.palette.grey[50],
    }),
    [theme],
  );

  return (
    <Card
      variant="outlined"
      component="article"
      aria-labelledby={titleId}
      sx={{
        borderRadius: 2,
        borderColor: "divider",
      }}
    >
      <CardContent
        sx={{
          p: 2,
          "&:last-child": { pb: 2 },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <HelpOutlineIcon
            sx={{
              fontSize: 20,
              color: "primary.main",
              opacity: 0.85,
              mt: 0.125,
              flexShrink: 0,
            }}
            aria-hidden
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id={titleId}
              component="h3"
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {props.question}
            </Typography>
            {props.questionHelper?.trim() ? (
              <Typography
                variant="caption"
                color="text.secondary"
                component="p"
                sx={{ mt: 0.75, display: "block", lineHeight: 1.5 }}
              >
                {props.questionHelper.trim()}
              </Typography>
            ) : null}

            <Box sx={answerSurfaceSx}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <ChatBubbleOutlineIcon
                  sx={{
                    fontSize: 18,
                    color: "text.secondary",
                    mt: 0.125,
                    flexShrink: 0,
                    opacity: 0.9,
                  }}
                  aria-hidden
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="p"
                  sx={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}
                >
                  {props.answer}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export interface PatientSelectedQuestionnaireViewProps {
  questionnaireResponse: fhir5.QuestionnaireResponse | undefined;
  usageTypeLabel?: string;
}

/** Conteúdo das respostas — reutilizado no dialog web e no ScreenMobile do sumário. */
export function PatientSelectedQuestionnaireView({
  questionnaireResponse,
  usageTypeLabel,
}: PatientSelectedQuestionnaireViewProps) {
  const qaPairs = useMemo(
    () => extractQaPairs(questionnaireResponse),
    [questionnaireResponse],
  );

  const title = useMemo(() => {
    if (!questionnaireResponse) return "Questionário";
    return questionnaireFallbackTitle(questionnaireResponse);
  }, [questionnaireResponse]);

  if (!questionnaireResponse) {
    return (
      <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
        Não foi possível localizar o questionário selecionado.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent
          sx={{
            p: { xs: 1.5, sm: 2 },
            "&:last-child": { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{ minWidth: 0, flex: 1, gap: 1 }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ flex: 1, minWidth: 0 }}
                component="h2"
              >
                {title}
              </Typography>
              {usageTypeLabel?.trim() ? (
                <Chip
                  label={usageTypeLabel}
                  size="small"
                  variant="outlined"
                  sx={{ flexShrink: 0, height: 28 }}
                />
              ) : null}
            </Stack>

            <Divider />

            <Typography variant="body2" color="text.secondary">
              {qaPairs.length} resposta(s)
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Stack spacing={1.5} component="section" aria-label="Respostas">
        {qaPairs.map((pair, idx) => (
          <QuestionAnswerBlock
            key={`${questionnaireResponse.id ?? "qr"}-q-${idx}`}
            questionnaireId={questionnaireResponse.id ?? "qr"}
            index={idx}
            question={pair.question}
            questionHelper={pair.questionHelper}
            answer={pair.answer}
          />
        ))}

        {qaPairs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Não há itens de resposta neste questionário.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

export interface PatientSelectedQuestionnaireDialogProps {
  open: boolean;
  onClose: () => void;
  questionnaireResponse: fhir5.QuestionnaireResponse | undefined;
  usageTypeLabel?: string;
}

const PatientSelectedQuestionnaireDialog = memo(
  ({
    open,
    onClose,
    questionnaireResponse,
    usageTypeLabel,
  }: PatientSelectedQuestionnaireDialogProps) => {
    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

    const paperSx = useMemo(
      () => ({
        maxHeight: isSmDown ? "100%" : { xs: "90vh", sm: "85vh" },
        height: isSmDown ? "100%" : "auto",
        display: "flex",
        flexDirection: "column" as const,
      }),
      [isSmDown],
    );

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        fullScreen={isSmDown}
        maxWidth="md"
        aria-labelledby="patient-selected-questionnaire-dialog-title"
        scroll="paper"
        PaperProps={{ sx: paperSx }}
      >
        <DialogTitle
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            id="patient-selected-questionnaire-dialog-title"
            variant="subtitle1"
            fontWeight={700}
            component="span"
          >
            Respostas do questionário
          </Typography>
          <IconButton
            aria-label="Fechar"
            edge="end"
            onClick={onClose}
            size="medium"
            sx={{
              color: "text.secondary",
              "&:focus-visible": {
                outline: 2,
                outlineOffset: 2,
                outlineColor: "primary.main",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            px: { xs: 2, sm: 3 },
            py: 2,
          }}
        >
          <PatientSelectedQuestionnaireView
            questionnaireResponse={questionnaireResponse}
            usageTypeLabel={usageTypeLabel}
          />
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={onClose}
            sx={{ minHeight: 44, textTransform: "none", px: 2.5 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

PatientSelectedQuestionnaireDialog.displayName =
  "PatientSelectedQuestionnaireDialog";

export { PatientSelectedQuestionnaireDialog };

